import * as React from 'react'
import toast from 'react-hot-toast'
import { PopupPortal } from '../Popup/PopupPortal.tsx'
import { AsyncButton } from '../AsyncButton/AsyncButton.tsx'
import {
    IAlertPreference,
    IAlertPreferenceDoc,
    getMyAlertPreferences,
    updateMyAlertPreferences,
    getContactAlertPreferences,
    updateContactAlertPreferences,
} from '../../../helpers/alertPreferencesHelper.ts'

interface Props {
    open: boolean
    setOpen: (o: boolean) => void
    deviceId: string
    petName: string
    /** If provided, we're editing a contact's preferences (owner-managed) */
    contactId?: string
    contactName?: string
}

export const AlertPreferences: React.FC<Props> = (props) => {
    const { open, setOpen, deviceId, petName, contactId, contactName } = props

    const [selectedCodes, setSelectedCodes] = React.useState<Set<string>>(new Set())
    const [initialCodes, setInitialCodes] = React.useState<Set<string>>(new Set())
    const [loading, setLoading] = React.useState(false)

    // Load preferences when popup opens
    React.useEffect(() => {
        if (!open || !deviceId) return

        const load = async () => {
            setLoading(true)
            let doc: IAlertPreferenceDoc | null = null

            if (contactId) {
                doc = await getContactAlertPreferences(deviceId, contactId)
            } else {
                doc = await getMyAlertPreferences(deviceId)
            }

            // If alerts array is empty or null, default to all ON
            const allCodes = ALERT_ORDER.map(item => item.code)

            if (doc?.alerts && doc.alerts.length > 0) {
                const enabledCodes = new Set(
                    doc.alerts
                        .filter(a => a.channels.sms)
                        .map(a => a.alertCode)
                )
                setSelectedCodes(enabledCodes)
                setInitialCodes(enabledCodes)
            } else {
                // No preferences set — show all as ON (backend default behavior)
                const allSet = new Set(allCodes)
                setSelectedCodes(allSet)
                setInitialCodes(allSet)
            }

            setLoading(false)
        }

        void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, deviceId, contactId])

    const toggleAlert = (alertCode: string) => {
        setSelectedCodes(prev => {
            const next = new Set(prev)
            if (next.has(alertCode)) {
                next.delete(alertCode)
            } else {
                next.add(alertCode)
            }
            return next
        })
    }

    const hasChanges = React.useMemo(() => {
        if (selectedCodes.size !== initialCodes.size) return true
        for (const code of selectedCodes) {
            if (!initialCodes.has(code)) return true
        }
        return false
    }, [selectedCodes, initialCodes])

    const save = async () => {
        // Build the full alerts array — include all alert types,
        // toggled ones get sms: true, others get sms: false
        const alerts: IAlertPreference[] = ALERT_ORDER.map((item) => ({
            alertCode: item.code,
            channels: {
                sms: selectedCodes.has(item.code),
                email: false,
                push: false,
            },
        }))

        let result: IAlertPreferenceDoc | null = null

        if (contactId) {
            result = await updateContactAlertPreferences(deviceId, contactId, alerts)
        } else {
            result = await updateMyAlertPreferences(deviceId, alerts)
        }

        if (result) {
            toast.success('Alert preferences saved')
            setOpen(false)
        } else {
            toast.error('Failed to save alert preferences')
        }
    }

    const cancel = () => {
        setOpen(false)
    }

    const title = contactName
        ? `${contactName}'s Alerts for ${petName}`
        : `My Alerts for ${petName}`

    // Fixed order and display labels for alerts
    const ALERT_ORDER: { code: string; label: string }[] = [
        { code: 'PowerUp', label: 'Power Up' },
        { code: 'PowerDown', label: 'Power Down' },
        { code: 'BatteryAlert', label: 'Low Battery' },
        { code: 'SafeZoneViolation', label: 'Safe Zone' },
        { code: 'BeaconInRange', label: 'Power Save On' },
        { code: 'BeaconOutOfRange', label: 'Power Save Off' },
        { code: 'FullCharge', label: 'Full Charge' },
        { code: 'InCharger', label: 'In Charger' },
        { code: 'OutOfCharger', label: 'Out of Charger' },
    ]

    const alertList = (
        <div className={'grid grid-cols-3 gap-2'}>
            {ALERT_ORDER.map((item) => (
                <div
                    key={item.code}
                    className={'form-control text-center bg-primary/10 rounded-lg p-2 cursor-pointer h-24 flex items-center justify-center'}
                    onClick={() => toggleAlert(item.code)}
                >
                    <div className="flex flex-col justify-center items-center gap-2">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-primary pointer-events-none"
                            checked={selectedCodes.has(item.code)}
                            readOnly
                        />
                        <div className="text-sm text-primary font-semibold leading-tight text-center">{item.label}</div>
                    </div>
                </div>
            ))}
        </div>
    )

    return (
        <PopupPortal open={open}>
            <div className={'flex flex-col gap-4 p-4 w-full'}>
                {/* Header */}
                <div className={'text-center'}>
                    <h2 className={'text-xl font-bold text-primary m-0'}>{title}</h2>
                </div>

                {/* Alert toggles */}
                {loading ? (
                    <div className={'flex justify-center py-8'}>
                        <span className="loading loading-spinner loading-md text-primary"></span>
                    </div>
                ) : (
                    alertList
                )}

                {/* Buttons */}
                <div className={'flex justify-between items-center pt-2'}>
                    <button
                        className={'btn btn-ghost btn-sm text-secondary'}
                        onClick={cancel}
                    >
                        Cancel
                    </button>
                    <AsyncButton
                        classNames={'btn-secondary btn-sm'}
                        onClick={save}
                        disabled={!hasChanges}
                    >
                        Save
                    </AsyncButton>
                </div>
            </div>
        </PopupPortal>
    )
}
